import API from '@/app-core/api/Api'
import CartCommon from '@/app-core/mixins/cart/cartCommon'
import {isArray, isObject} from '@/app-core/lib/utils/type'
import NotificationTypeEnum from '@/app-core/api/enums/NotificationTypeEnum'
import StoreCommon from '@/app-core/mixins/store/storeCommon'
import CheckoutPaymentModel from '@/app-core/api/models/checkout/CheckoutPaymentModel'
import GuestModel from '@/app-core/api/models/customer/GuestModel'
import AnalyticsCommon from '@/app-core/mixins/analytics/analyticsCommon'
import AuthOtp from '@/app-core/mixins/auth/authOtp'
import {isValidMobilePhone} from '@/app-core/api/validations/CustomValidator'
import AccountSettingsModel from '@/app-core/api/models/account/AccountSettingsModel'

export default {
  name: 'PaymentGatewayCommon',

  mixins: [AuthOtp, CartCommon, StoreCommon, AnalyticsCommon],

  props: {
    form: {
      type   : Object,
      default: undefined
    },

    model: {
      type   : Object,
      default: undefined
    }
  },

  data() {
    return {
      loading : false,
      disabled: false
    }
  },

  methods: {
    completeOrder() {
      if (!this.isValidPayment()) return

      this.requestData()
    },

    saveFromData() {
      if (this.userIsAuthenticated && this.user?.Info) {
        const model = new AccountSettingsModel(this.user.Info)
        model.PhoneValidated = this.model.PhoneValidated ?? false
        model.EmailValidated = this.model.EmailValidated ?? false
        model.SaveSettings()
      }

      this.submitData()
    },

    submitData(data = {}) {
      if (this.loading || (!this.AppConfig.Marketplace.AllowGuestOrders && !this.userIsAuthenticated) || !this.$parent.validateForm()) return
      if (typeof this.$parent.clearServerErrorMessages === 'function') this.$parent.clearServerErrorMessages()
      this.loading = true

      const paymentDataModel = new CheckoutPaymentModel(this.$parent.model.clone())
      if (this.userIsAuthenticated) {
        paymentDataModel.Customer = new GuestModel(this.user.Info)
      }

      const payload = {...paymentDataModel, ...(data || {})}

      API.Resource.Checkout.Payment(payload)
        .then(response => {
          if (API.isResponseSuccess(response) && !!API.responseData(response)?.Success) {
            this.setPurchaseGA(API.responseData(response)?.OrderId, paymentDataModel?.Customer)
            this.requestSuccess(response)
          } else {
            this.loading = false
            this.requestError(response)
          }
        })
        .catch(() => {
          this.loading = false
          this.onRequestError()
        })
        .finally(() => {
        })
    },

    requestData() {
      if (this.AppConfig?.Auth?.Otp?.Sms || this.AppConfig?.Auth?.Otp?.Email) {
        if (this.phoneNeedsOtpValidation || this.emailNeedsOtpValidation) {
          if (!this.$parent.validateForm()) return
          this.otpRequest()
        } else {
          this.submitData()
        }
      } else {
        this.submitData()
      }
    },

    requestSuccess(response) {
      this.onRequestSuccess(response)
    },

    onRequestSuccess(response) {
      this.requestUpdateCart(response)
      this.requestSuccessNotify(response)
      this.parseRequestMessages(response)
      this.$router.replace({name: 'CheckoutSuccess'})
    },

    requestError(response) {
      this.onRequestError(response)
    },

    onRequestError(response) {
      this.parseRequestMessages(response)
    },

    requestUpdateCart(response) {
      // this.$bus.$emit('cart:set-data', API.responseData(response)?.Cart)
      this.$bus.$emit('cart:get-data')
    },

    requestSuccessNotify(response) {
      this.$bus.$emit('cart:payment-gateway:response', API.responseData(response))
    },

    parseRequestMessages(response) {
      if (response) {
        const ResponseMessages = API.responseData(response)?.Result || []

        if (isObject(ResponseMessages)) {
          if (ResponseMessages.Success || ResponseMessages?.Messages?.Success) {
            this.showResponseNotification(ResponseMessages.Success || ResponseMessages?.Messages?.Success, '', '', '', NotificationTypeEnum.SUCCESS)
          } else if (ResponseMessages.Info || ResponseMessages?.Messages?.Info) {
            this.showResponseNotification(ResponseMessages.Info || ResponseMessages?.Messages?.Info, '', '', '', NotificationTypeEnum.INFO)
          } else if (ResponseMessages.Warning || ResponseMessages?.Messages?.Warning) {
            this.showResponseNotification(ResponseMessages.Warning || ResponseMessages?.Messages?.Warning, '', '', '', NotificationTypeEnum.WARNING)
          } else if (ResponseMessages.Error || ResponseMessages?.Messages?.Error) {
            this.showResponseNotification(ResponseMessages.Error || ResponseMessages?.Messages?.Error, '', '', '', NotificationTypeEnum.ERROR)
          }
        }

        if (isArray(ResponseMessages)) {
          ResponseMessages.forEach(item => {
            const store = this.getStoreById(item.StoreId)

            if (item.Messages?.Success) {
              this.showResponseNotification(item.Messages.Success, store.Name || '', '', store.Logo || '', NotificationTypeEnum.SUCCESS)
            } else if (item.Messages?.Info) {
              this.showResponseNotification(item.Messages.Info, store.Name || '', '', store.Logo || '', NotificationTypeEnum.INFO)
            } else if (item.Messages?.Warning) {
              this.showResponseNotification(item.Messages.Warning, store.Name || '', '', store.Logo || '', NotificationTypeEnum.WARNING)
            } else if (item.Messages?.Error) {
              this.showResponseNotification(item.Messages.Error, store.Name || '', '', store.Logo || '', NotificationTypeEnum.ERROR)
            }

            // if (item.Fetch) this.getStoreMenu(item.StoreId)
          })
        }

        this.requestUpdateCart(response)
      }
    },

    showResponseNotification(message, title, icon, img, type = 'success') {
      if (!message) return

      this.$bus.$emit('app:show-notification', {
        title: title || '',
        body : message || '',
        type : type || 'success',
        icon : icon || '',
        img  : img || ''
      })
    },

    isValidPayment() {
      let isValid = true

      if (this.selectedPaymentMethod) {
        if (!this.isMinimumOrderTotalValid) {
          this.setPaymentTypeErrorMessage(this.selectedPaymentMethod)
          isValid = false
        }
      } else {
        isValid = false
      }

      return isValid
    },

    setPaymentTypeErrorMessage(payment) {
      if (!payment) return

      const paymentMethodName = payment.lang && payment.lang[this.$i18n.locale] ? payment.lang[this.$i18n.locale].name : payment.name
      const minimumOrderTotal = this.$options.filters.currency((payment.data && payment.data.minimum_order_total) ? payment.data.minimum_order_total : 0)
      const minimumOrderTotalDiff = this.$options.filters.currency(this.orderMinTotalAmountDiffPayment(payment))

      this.$bus.$emit('app:show-notification', {
        title: this.$t('Cart.Message.Info.MinimumOrder.Title'),
        body : this.$t('Cart.Message.Info.MinimumOrder.Body', {
          paymentMethodName    : paymentMethodName,
          minimumOrderTotal    : minimumOrderTotal,
          minimumOrderTotalDiff: minimumOrderTotalDiff
        }),
        type   : 'error',
        icon   : '',
        timeout: 5000
      })
    }
  },

  computed: {
    phoneNeedsOtpValidation() {
      const phoneValid = isValidMobilePhone(this.model.Phone, this.model)
      return this.AppConfig?.Auth?.Otp?.Sms && phoneValid && (!this.userIsAuthenticated || (this.userIsAuthenticated && !this.model.PhoneValidated))
    },

    emailNeedsOtpValidation() {
      return this.AppConfig?.Auth?.Otp?.Email && (!this.userIsAuthenticated || (this.userIsAuthenticated && !this.model.EmailValidated))
    },

    modelForm() {
      return this.form
    }
  }
}
